import { Icon } from "@storeblocks/icon";
import { Icons } from "@storebrand-digital/insurance-definitions";
import { ProductCategory } from "Components/Features/CheckPrice/Products/RiskProducts/Valuables/valuablesConstants";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { ProductType } from "Store/interfaces/products/checkout/ProductType";
import { ValuablesProduct } from "Store/interfaces/products/checkout/riskProducts/ValuablesProduct";
import React from "react";
import { isManualProduct } from "./productUtils";

interface Props {
  product: ProductType;
  label: string;
}

export const valuableProductIcons = {
  [ProductCategory.NATIONAL_COSTUME]: Icons.NATIONAL_COSTUME,
  [ProductCategory.FUR_COAT]: Icons.FUR_COAT,
  [ProductCategory.CAMERA]: Icons.CAMERA,
  [ProductCategory.HEARING_AID]: Icons.HEARING_AID,
  [ProductCategory.COMPUTER_EQUIPMENT]: Icons.COMPUTER_EQUIPMENT,
  [ProductCategory.GOLF_EQUIPEMENT]: Icons.GOLF_EQUIPEMENT,
  [ProductCategory.FIREARM]: Icons.FIREARM,
  [ProductCategory.BIKE]: Icons.BIKE,
  [ProductCategory.EL_BIKE]: Icons.EL_BIKE,
  [ProductCategory.OTHER]: Icons.OTHER,
};

function getValuableIcon(productCategory: string): Icons {
  return valuableProductIcons[productCategory as ProductCategory];
}

export const ProductIcon = ({
  product,
  label,
}: Props): React.ReactElement | null => {
  const iconName = findIconFor(product);

  if (iconName) {
    return <Icon name={iconName} aria-label={label} />;
  }
  return null;
};

export const findIconFor = (product: ProductType): Icons | undefined => {
  const productName = isManualProduct(product)
    ? product.insuranceName
    : product.name;

  switch (productName) {
    case INSURANCE_NAME_VALUE.ANIMAL:
      return Icons.ANIMAL;
    case INSURANCE_NAME_VALUE.BOAT:
      return Icons.BOAT;
    case INSURANCE_NAME_VALUE.CAR:
    case INSURANCE_NAME_VALUE.CAR_NORSK_ELBILFORENING:
      return Icons.CAR;
    case INSURANCE_NAME_VALUE.AKA_CHILD:
    case INSURANCE_NAME_VALUE.CHILD:
    case INSURANCE_NAME_VALUE.NEW_CHILD:
      return Icons.CHILD;
    case INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN:
      return Icons.PREGNANT_AND_NEWBORN;
    case INSURANCE_NAME_VALUE.DEATH:
      return Icons.DEATH;
    case INSURANCE_NAME_VALUE.ELECTRIC_SCOOTER:
      return Icons.ELECTRIC_SCOOTER;
    case INSURANCE_NAME_VALUE.HOUSE:
    case INSURANCE_NAME_VALUE.RENTAL:
      return Icons.HOUSE;
    case INSURANCE_NAME_VALUE.CABIN:
    case INSURANCE_NAME_VALUE.CABIN_CONTENTS:
      return Icons.CABIN;
    case INSURANCE_NAME_VALUE.HOUSE_CONTENTS:
    case INSURANCE_NAME_VALUE.AKA_HOUSE_CONTENTS:
    case INSURANCE_NAME_VALUE.HUSEIERNE_HOUSE_CONTENTS:
      return Icons.HOUSE_CONTENT;
    case INSURANCE_NAME_VALUE.TRACTOR:
      return Icons.TRACTOR;
    case INSURANCE_NAME_VALUE.TRAVEL:
    case INSURANCE_NAME_VALUE.AKA_TRAVEL:
    case INSURANCE_NAME_VALUE.AKA_STUDENT:
      return Icons.TRAVEL;
    case INSURANCE_NAME_VALUE.MOPED:
      return Icons.MOPED;
    case INSURANCE_NAME_VALUE.CARAVAN:
      return Icons.CARAVAN;
    case INSURANCE_NAME_VALUE.MOTORCYCLE:
      return Icons.MOTORCYCLE;
    case INSURANCE_NAME_VALUE.TRAILER:
      return Icons.TRAILER;
    case INSURANCE_NAME_VALUE.VALUABLES:
      const valuablesProduct = product as ValuablesProduct;
      return getValuableIcon(valuablesProduct.valuableType);
    case INSURANCE_NAME_VALUE.ACCIDENT:
    case INSURANCE_NAME_VALUE.AKA_FAMILY_ACCIDENT:
      return Icons.ACCIDENT;
    case INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS:
    case INSURANCE_NAME_VALUE.CRITICAL_ILLNESS:
      return Icons.CRITICAL_ILLNESS;
    case INSURANCE_NAME_VALUE.AKA_GROUP_LIFE:
    case INSURANCE_NAME_VALUE.AKA_HEALTH:
    case INSURANCE_NAME_VALUE.AKA_HEALTH_WITHOUT_SIGNING:
    case INSURANCE_NAME_VALUE.CHILD_HEALTH:
    case INSURANCE_NAME_VALUE.HEALTH:
    case INSURANCE_NAME_VALUE.HEALTH_WITHOUT_SIGNING:
    case INSURANCE_NAME_VALUE.SOP_SICKNESS_LEAVE:
      return Icons.HEALTH;
    case INSURANCE_NAME_VALUE.DISABILITY_FLEX:
      return Icons.DISABILITY;
    case INSURANCE_NAME_VALUE.VETERAN_AUTO:
      return Icons.VETERAN;
    default:
      return undefined;
  }
};
